import React, { useEffect, useState, useCallback } from "react";
import {
  Checkbox,
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ClientModel from "../../core/api/clients/ClientModel";
import { translations } from "../../translations/common";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const StyledFormControl = styled(FormControl)(() => ({
  minWidth: 200,
}));

interface ClientEmailSelectProps {
  id: string;
  name: string;
  clientId: string;
  values: string[];
  onValueChange: (emails: string[]) => void;
  touched?: boolean;
  errorMessage?: string | string[];
}

const menuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
  variant: "menu" as const,
  getContentAnchorEl: null,
};

/**
 * A form control component for selecting multiple email addresses from a client's email list
 */
export const ClientEmailSelect: React.FC<ClientEmailSelectProps> = ({
  id,
  name,
  clientId,
  values,
  touched,
  errorMessage,
  onValueChange,
}) => {
  const [emails, setEmails] = useState<string[]>([]);

  const fetchClientEmails = useCallback(async () => {
    try {
      const client = await ClientModel.findOne(clientId);
      setEmails(client.emails);
    } catch (error) {
      console.error('Failed to fetch client emails:', error);
      setEmails([]);
    }
  }, [clientId]);

  useEffect(() => {
    fetchClientEmails();
  }, [fetchClientEmails]);

  const handleChange = useCallback((event: SelectChangeEvent<string[]>) => {
    onValueChange(event.target.value as string[]);
  }, [onValueChange]);

  const renderValue = useCallback((selected: unknown) => {
    return (selected as string[]).join(", ");
  }, []);

  return (
    <StyledFormControl>
      <InputLabel id={`${id}-label`} sx={{ pl: 1 }}>{translations.clientEmailSelect.recipients}</InputLabel>
      <Select<string[]>
        fullWidth
        id={id}
        name={name}
        labelId={`${id}-label`}
        multiple
        value={values}
        onChange={handleChange}
        input={<Input />}
        renderValue={renderValue}
        MenuProps={menuProps}
        aria-placeholder={translations.clientEmailSelect.mainEmail}
      >
        {emails.map((email) => (
          <MenuItem key={email} value={email}>
            <Checkbox checked={values.includes(email)} />
            <ListItemText primary={email} />
          </MenuItem>
        ))}
      </Select>
      {errorMessage && touched && (
        <FormHelperText error>{errorMessage}</FormHelperText>
      )}
    </StyledFormControl>
  );
};
